import type { ReferencedEntity } from "@cohesive/api-client";

export const FILE_RECORD_TEXT_SELECTION_ENTITY_TYPE =
  "file_record_text_selection" as const;

export const LEGACY_ARTIFACT_TEXT_SELECTION_ENTITY_TYPE =
  "artifact_text_selection" as const;

export type FileRecordTextSelectionCache = {
  fileRecordId: string;
  fileRecordName: string;
  selectedText: string;
  /** Unique per excerpt; omitted on legacy single-selection cache entries. */
  selectionId?: string;
};

export function fileRecordTextSelectionQueryKey(canvasId: string) {
  return ["file-record-text-selection", canvasId] as const;
}

/** Normalizes legacy single-object cache entries to an array. */
export function normalizeFileRecordTextSelectionCache(
  data: unknown,
): FileRecordTextSelectionCache[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (
    typeof data === "object" &&
    data !== null &&
    "fileRecordId" in data &&
    "selectedText" in data
  ) {
    return [data as FileRecordTextSelectionCache];
  }
  return [];
}

export function fileRecordTextSelectionEntityId(
  entry: FileRecordTextSelectionCache,
): string {
  if (entry.selectionId) {
    return `${entry.fileRecordId}:selection:${entry.selectionId}`;
  }
  return `${entry.fileRecordId}:selection`;
}

export function isTextSelectionEntity(entity: ReferencedEntity): boolean {
  return (
    entity.entity_type === FILE_RECORD_TEXT_SELECTION_ENTITY_TYPE ||
    entity.entity_type === LEGACY_ARTIFACT_TEXT_SELECTION_ENTITY_TYPE
  );
}

/** Entity ids for text excerpts attached to a message (for selective cache eviction). */
export function textSelectionEntityIdsFromReferencedEntities(
  entities: ReferencedEntity[] | undefined,
): string[] {
  return (entities ?? [])
    .filter(isTextSelectionEntity)
    .map((entity) => entity.entity_id);
}

export function isTextSelectionEntityId(entityId: string): boolean {
  return entityId.includes(":selection");
}

export function textSelectionSelectedText(entity: ReferencedEntity): string {
  const entry = entity.metadata?.[0];
  if (!entry || typeof entry !== "object" || !("selected_text" in entry)) {
    return "";
  }
  return String((entry as { selected_text?: unknown }).selected_text ?? "");
}

export function textSelectionPillLabel(entity: ReferencedEntity): string {
  const t = textSelectionSelectedText(entity);
  const preview = t.slice(0, 30);
  return `${preview}${t.length > 30 ? "…" : ""}`;
}

export function buildFileRecordTextSelectionEntity(params: {
  fileRecordId: string;
  fileRecordName: string;
  selectedText: string;
  selectionId?: string;
}): ReferencedEntity {
  const cacheEntry: FileRecordTextSelectionCache = {
    fileRecordId: params.fileRecordId,
    fileRecordName: params.fileRecordName,
    selectedText: params.selectedText,
    selectionId: params.selectionId,
  };
  return {
    entity_id: fileRecordTextSelectionEntityId(cacheEntry),
    entity_type: FILE_RECORD_TEXT_SELECTION_ENTITY_TYPE,
    platform: "cohesive",
    name: params.fileRecordName,
    metadata: [{ selected_text: params.selectedText }],
  };
}

export function buildFileRecordTextSelectionEntityFromCache(
  cache: FileRecordTextSelectionCache,
): ReferencedEntity {
  return buildFileRecordTextSelectionEntity({
    fileRecordId: cache.fileRecordId,
    fileRecordName: cache.fileRecordName,
    selectedText: cache.selectedText,
    selectionId: cache.selectionId,
  });
}

export function buildFileRecordTextSelectionEntitiesFromCache(
  cache: FileRecordTextSelectionCache[],
): ReferencedEntity[] {
  return cache.map(buildFileRecordTextSelectionEntityFromCache);
}
